// Claude: Admin Reward Plans CRUD page — mirrors Subscriptions.tsx 27-02-2026
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  Switch,
  Text,
} from "@chakra-ui/react";
import { Modal, Table } from "antd";
import React, { useEffect, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { RewardPlan } from "../../Interface/AdminInterfaces";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { AdminAPI } from "../../Apis/adminAPI";
import Loader from "../../Components/Loader/Loader";
import { errorHandler } from "../../Utils/handler";

type Props = {};

const emptyForm: RewardPlan = {
  name: "",
  rewardPoints: 0,
  rewardPointValue: 0,
  recurringRewards: false,
  currency: "",
};

const RewardPlans = (props: Props) => {
  const { error, success } = useToastWrapper();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState("");
  const [formData, setFormData] = useState<RewardPlan>({ ...emptyForm });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const showModal = () => setIsModalVisible(true);
  const handleOk = () => { setIsModalVisible(false); setFormData({ ...emptyForm }); setEdit(false); setEditId(""); };

  const getData = async () => {
    setLoading(true);
    try {
      const res = await AdminAPI.getRewardPlans();
      setData(res?.data?.data ?? []);
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) { error("Plan name is required", ""); return; }
    setLoading(true);
    try {
      if (edit) {
        await AdminAPI.updateRewardPlan(editId, formData);
        success("Reward plan updated", "");
      } else {
        await AdminAPI.addRewardPlan(formData);
        success("Reward plan added", "");
      }
      handleOk();
      getData();
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await AdminAPI.deleteRewardPlan(id);
      success("Reward plan deleted", "");
      getData();
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Points / Meeting", dataIndex: "rewardPoints", key: "rewardPoints" },
    { title: "Value / Point", dataIndex: "rewardPointValue", key: "rewardPointValue" },
    { title: "Currency", dataIndex: "currency", key: "currency" },
    { title: "Recurring", dataIndex: "recurringRewards", key: "recurringRewards", render: (v: boolean) => v ? "Yes" : "No" },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Flex gap={1}>
          <Button
            h="auto" px={3} py={1} bg="#0E122F" color="white" fontSize="xs"
            _hover={{ bg: "#2E7D32" }}
            onClick={() => {
              setFormData({
                name: record.name,
                rewardPoints: record.rewardPoints,
                rewardPointValue: record.rewardPointValue,
                recurringRewards: record.recurringRewards,
                currency: record.currency,
              });
              setEdit(true);
              setEditId(record._id);
              showModal();
            }}
          >Edit</Button>
          <Button
            h="auto" px={3} py={1} bg="secondary.light" color="secondary.main" fontSize="xs"
            _hover={{ bg: "#C62828", color: "white" }}
            onClick={() => { setDeleteId(record._id); setIsDeleteModalOpen(true); }}
          >Delete</Button>
        </Flex>
      ),
    },
  ];

  useEffect(() => { getData(); }, []);

  return (
    <div className="p-4">
      {loading ? <Loader /> : null}

      <Modal title="Alert" footer={null} onCancel={() => setIsDeleteModalOpen(false)} open={isDeleteModalOpen}>
        <Text my={5} mt={8}>Are you sure you want to delete this reward plan?</Text>
        <Flex justifyContent="flex-end" gap={3}>
          <Button h="auto" px={4} py={2} variant="outline" borderColor="#0E122F" color="#0E122F" _hover={{ bg: "gray.100" }} onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
          <Button h="auto" px={4} py={2} bg="#C62828" color="white" _hover={{ bg: "red.800" }} onClick={() => { handleDelete(deleteId); setIsDeleteModalOpen(false); }}>Yes, Delete</Button>
        </Flex>
      </Modal>

      <Modal
        footer={null}
        title={edit ? "Edit Reward Plan" : "Add Reward Plan"}
        width="40%"
        open={isModalVisible}
        onCancel={handleOk}
        centered
      >
        <Box padding={2}>
          <Grid gap={4} templateColumns="repeat(1, 1fr)">
            <GridItem>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" fontWeight="500">Plan Name <span style={{ color: "red" }}>*</span></Text>
                <Input width="55%" height="40px" placeholder="e.g. Standard" name="name" value={formData.name} onChange={handleChange} />
              </Flex>
            </GridItem>
            <GridItem>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" fontWeight="500">Points per Closed Meeting</Text>
                <Input width="55%" height="40px" type="number" name="rewardPoints" value={formData.rewardPoints} onChange={handleChange} />
              </Flex>
            </GridItem>
            <GridItem>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" fontWeight="500">Value per Point</Text>
                <Input width="55%" height="40px" type="number" name="rewardPointValue" value={formData.rewardPointValue} onChange={handleChange} />
              </Flex>
            </GridItem>
            <GridItem>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" fontWeight="500">Currency</Text>
                <Input width="55%" height="40px" placeholder="e.g. Rs." name="currency" value={formData.currency} onChange={handleChange} />
              </Flex>
            </GridItem>
            <GridItem>
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" fontWeight="500">Recurring Rewards</Text>
                <Switch
                  size="lg"
                  isChecked={formData.recurringRewards}
                  onChange={() => setFormData({ ...formData, recurringRewards: !formData.recurringRewards })}
                />
              </Flex>
            </GridItem>
          </Grid>
          <Flex justifyContent="flex-end" mt={8}>
            <Button h="40px" bg="#0E122F" color="white" px={4} _hover={{ bg: "#2E7D32" }} onClick={handleSubmit}>Submit</Button>
          </Flex>
        </Box>
      </Modal>

      <Flex alignItems="center" gap={2} mt={2} mb={4}>
        <Text fontSize="md" fontWeight={600}>Reward Plans</Text>
        <Button
          size="sm" fontSize="xs" h="28px" background="#0E122F" color="white" px={3} gap={2}
          _hover={{ background: "#2E7D32" }}
          onClick={() => { setEdit(false); setFormData({ ...emptyForm }); setEditId(""); showModal(); }}
        >
          <AiOutlinePlus /> Add Plan
        </Button>
      </Flex>

      <Box sx={{ "& .ant-table": { fontSize: "12px" }, "& .ant-table-cell": { fontSize: "12px" } }}>
        <Table size="small" style={{ width: "100%" }} tableLayout="fixed" dataSource={data} columns={columns} bordered pagination={false} rowKey="_id" />
      </Box>
    </div>
  );
};

export default RewardPlans;
